import { uuidv7, type Label, type Session, type Settings } from '@timato/shared';
import type { TimatoDB } from './db';

/** v1 (../timato) kept everything in localStorage under these keys, sessions limited to the last 30 days. */
export const V1_KEYS = {
  settings: 'tickdownPomodoroSettings',
  sessions: 'tickdownPomodoroSessions',
  activeTimer: 'tickdownPomodoroActiveTimer',
} as const;
const META_KEY = 'v1Imported';

type V1Session = {
  id: string; type: 'work' | 'shortBreak' | 'longBreak'; duration: number; startTime: string; endTime?: string;
  completed: boolean; task?: string; tags?: string[];
};
type V1Settings = Partial<{
  workDuration: number; shortBreakDuration: number; longBreakDuration: number; sessionsUntilLongBreak: number;
  autoStartBreaks: boolean; soundEnabled: boolean; notificationEnabled: boolean;
}>;

const MODE = { work: 'focus', shortBreak: 'short', longBreak: 'long' } as const;

export function mapV1Session(s: V1Session, labelIdByName: Map<string, string>, stamp: string): Session {
  const startedAt = new Date(s.startTime);
  const durationSec = Math.max(0, Math.round((s.duration ?? 0) * 60));
  const endedAt = s.endTime ? new Date(s.endTime) : new Date(startedAt.getTime() + durationSec * 1000);
  const mode = MODE[s.type] ?? 'focus';
  const status = mode === 'focus' ? (s.completed ? 'completed' : 'skipped') : 'break';
  return {
    id: uuidv7(startedAt.getTime()),
    teamId: null, taskId: null, projectId: null,
    mode, status,
    startedAt: startedAt.toISOString(), endedAt: endedAt.toISOString(),
    durationSec: s.endTime ? Math.max(0, Math.round((endedAt.getTime() - startedAt.getTime()) / 1000)) : durationSec,
    goalText: mode === 'focus' ? (s.task ?? '') : '',
    labelIds: (s.tags ?? []).map((t) => labelIdByName.get(t.trim().toLowerCase())).filter((x): x is string => !!x),
    source: 'timer', editedAt: null, editedBy: null,
    updatedAt: stamp, deletedAt: null,
  };
}

export function mapV1Settings(v: V1Settings, current: Settings): Settings {
  const clamp = (n: unknown, d: number) => (typeof n === 'number' && n >= 1 && n <= 90 ? Math.round(n) : d);
  const after = [2, 3, 4, 5].includes(v.sessionsUntilLongBreak as number) ? (v.sessionsUntilLongBreak as 2 | 3 | 4 | 5) : current.longAfter;
  return {
    ...current,
    focusMin: clamp(v.workDuration, current.focusMin),
    shortMin: clamp(v.shortBreakDuration, current.shortMin),
    longMin: clamp(v.longBreakDuration, current.longMin),
    longAfter: after,
    autoStartBreaks: typeof v.autoStartBreaks === 'boolean' ? v.autoStartBreaks : current.autoStartBreaks,
    sound: typeof v.soundEnabled === 'boolean' ? v.soundEnabled : current.sound,
    notifications: typeof v.notificationEnabled === 'boolean' ? v.notificationEnabled : current.notifications,
  };
}

/**
 * One-time import of v1 data found on this origin. Idempotent: marks `meta.v1Imported` and never runs twice.
 * Leaves the v1 keys in place (harmless; the user may still have the old tab open).
 */
export async function importV1IfPresent(db: TimatoDB, stamp: () => string, storage: Storage = localStorage): Promise<{ sessions: number; labels: number; settings: boolean } | null> {
  if (await db.meta.get(META_KEY)) return null;
  const rawSessions = storage.getItem(V1_KEYS.sessions);
  const rawSettings = storage.getItem(V1_KEYS.settings);
  if (!rawSessions && !rawSettings) { await db.meta.put({ key: META_KEY, value: { at: new Date().toISOString(), found: false } }); return null; }

  let v1Sessions: V1Session[] = [];
  let v1Settings: V1Settings = {};
  try { v1Sessions = rawSessions ? (JSON.parse(rawSessions) as V1Session[]) : []; } catch { v1Sessions = []; }
  try { v1Settings = rawSettings ? (JSON.parse(rawSettings) as V1Settings) : {}; } catch { v1Settings = {}; }

  const tagNames = new Map<string, string>();
  for (const s of v1Sessions) for (const t of s.tags ?? []) { const k = t.trim(); if (k && !tagNames.has(k.toLowerCase())) tagNames.set(k.toLowerCase(), k); } // first-seen casing wins

  const result = await db.transaction('rw', [db.sessions, db.labels, db.settings, db.meta], async () => {
    const labelIdByName = new Map<string, string>();
    const labels: Label[] = [...tagNames.entries()].map(([key, name], i) => {
      const id = uuidv7();
      labelIdByName.set(key, id);
      return { id, teamId: null, name, tint: null, position: i, retiredAt: null, updatedAt: stamp(), deletedAt: null };
    });
    if (labels.length) await db.labels.bulkPut(labels);

    const sessions = v1Sessions.filter((s) => s?.startTime && !Number.isNaN(Date.parse(s.startTime))).map((s) => mapV1Session(s, labelIdByName, stamp()));
    if (sessions.length) await db.sessions.bulkPut(sessions);

    let settingsChanged = false;
    if (rawSettings) {
      const current = (await db.settings.get('settings')) ?? defaultSettings(stamp());
      await db.settings.put({ ...mapV1Settings(v1Settings, current), updatedAt: stamp() });
      settingsChanged = true;
    }
    await db.meta.put({ key: META_KEY, value: { at: new Date().toISOString(), found: true, sessions: sessions.length, labels: labels.length } });
    return { sessions: sessions.length, labels: labels.length, settings: settingsChanged };
  });
  return result;
}

export function defaultSettings(stamp: string): Settings {
  return {
    id: 'settings', focusMin: 25, shortMin: 5, longMin: 15, longAfter: 4, autoStartBreaks: true, sound: true, notifications: true,
    theme: 'system', timerStyle: 'ring', tabTitleSeconds: true, timeFormat: '24h', dateFormat: 'dmy', longRunReminderMin: 0, updatedAt: stamp, deletedAt: null,
  };
}
